import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, NgZone, OnInit, PLATFORM_ID } from '@angular/core';

import { SettingService } from '../services/setting.service';

type UserData = {
  email: string;
  firstName: string;
  lastName: string;
};

declare var window: any;
declare var embeddedservice_bootstrap: any;

// @dynamic
@Component({
  selector: 'live-chat',
  template: ''
})
export class ChatComponent implements OnInit {
  constructor(
    public settingService: SettingService,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly ngZone: NgZone
  ) { }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      // Expose global init function to match the snippet's onload behavior
      window.initEmbeddedMessaging = () => {
        try {
          embeddedservice_bootstrap.settings =
            embeddedservice_bootstrap.settings || {};
          embeddedservice_bootstrap.settings.language = 'en_US';

          embeddedservice_bootstrap.init(
            this.settingService.orgId,
            this.settingService.messagingName,
            this.settingService.messagingUrl,
            {
              scrt2URL: this.settingService.scrt2Url
            }
          );
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err);
        }
      };

      // Pre-chat setup: visible/hidden fields and validation
      const setupPrechat = () => {
        const ALLOWED_PRODUCTS = [
          'Asset Recovery',
          'Auto',
          'GAP',
          'MMP',
          'Mortgage',
          'Payment Protection (Credit Insurance/Debt Cancellation)',
          'Payment Protection Claims (Credit Insurance/Debt Cancellation)',
          'Payments by SWIVEL',
          'Portal Support'
        ];
        const trimSafe = (v: any) => (typeof v === 'string' ? v.trim() : v);

        window.addEventListener('onEmbeddedMessagingReady', () => {
          try {
            embeddedservice_bootstrap.settings =
              embeddedservice_bootstrap.settings || {};
            embeddedservice_bootstrap.settings.language = 'en_US';

            const { email, firstName, lastName } = this.getUserData();

            embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
              _firstName: { value: firstName || '', isEditableByEndUser: true },
              _lastName: { value: lastName || '', isEditableByEndUser: true },
              _email: { value: email || '', isEditableByEndUser: true },
              _subject: { value: '', isEditableByEndUser: true },
              Products: { value: '', isEditableByEndUser: true }
            });

            // Optional hidden fields
            embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
              Source: { value: 'WebChat' }
            });

            // Validate required fields before conversation starts
            embeddedservice_bootstrap.prechatAPI.onPrechatSubmit(
              (fields: any) => {
                const firstName = trimSafe(fields['_firstName']);
                const lastName = trimSafe(fields['_lastName']);
                const email = trimSafe(fields['_email']);
                const subject = trimSafe(fields['_subject']);
                let product = trimSafe(fields['Products']);

                const missingLabels: string[] = [];
                if (!firstName) missingLabels.push('First Name');
                if (!lastName) missingLabels.push('Last Name');
                if (!email) missingLabels.push('Email');
                if (!subject) missingLabels.push('Subject');
                if (!product) missingLabels.push('Products');

                if (missingLabels.length > 0) {
                  alert(
                    'Please fill in all required fields: ' +
                    missingLabels.join(', ')
                  );
                  return false;
                }

                const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
                if (!emailOk) {
                  alert('Please enter a valid email address.');
                  return false;
                }

                if (!ALLOWED_PRODUCTS.includes(product)) {
                  const ciMatch = ALLOWED_PRODUCTS.find(
                    p =>
                      String(p).toLowerCase() === String(product).toLowerCase()
                  );
                  if (ciMatch) product = ciMatch;
                  else {
                    alert(
                      'Please select a valid Products value.\nAllowed:\n- ' +
                      ALLOWED_PRODUCTS.join('\n- ')
                    );
                    return false;
                  }
                }

                // Ensure exact label is submitted
                fields['Products'] = product;
                return true;
              }
            );
          } catch (err) {
            console.error('Messaging pre-chat setup error:', err);
          }
        });
      };

      setupPrechat();

      // Inject messaging bootstrap script and call init when loaded (component-driven
      // embed; the onload/else both call the guarded window.initEmbeddedMessaging()).
      const messagingScriptUrl = this.settingService.messagingScriptUrl;
      if (
        messagingScriptUrl &&
        !this.document.querySelector(`script[src="${messagingScriptUrl}"]`)
      ) {
        const ms = this.document.createElement('script');
        ms.type = 'text/javascript';
        ms.src = messagingScriptUrl;
        ms.onload = () => {
          try {
            if (typeof window.initEmbeddedMessaging === 'function') {
              window.initEmbeddedMessaging();
            }
          } catch (err) {
            console.error('Messaging script onload error:', err);
          }
        };
        this.document.body.appendChild(ms);
      } else if (messagingScriptUrl) {
        try {
          if (typeof window.initEmbeddedMessaging === 'function') {
            window.initEmbeddedMessaging();
          }
        } catch (err) {
          console.error('Messaging init error:', err);
        }
      }
    });
  }

  getUserData(): UserData {
    try {
      const appName = sessionStorage.getItem('appName');
      const userData = JSON.parse(sessionStorage.getItem(appName))?.userData;
      const formattedUserData: UserData = {
        email: userData.email,
        firstName: userData.given_name,
        lastName: userData.family_name
      };

      return formattedUserData;
    } catch {
      console.info('Unable to fetch User Info for Live Chat');
    }
  }
}
